import { BaJwt } from '@oiambk/oiam-oauth-types';
import log from 'loglevel';
import { AuthRequestReason } from '../model/constants';
import { ERR_LOOP_DETECTED } from '../model/errors';
import { LoopBreakerData } from '../model/loop-breaker-data';
import { EServiceConfig } from './eservice-config';
import { NginxErrorLogger } from './nginx-error-logger';

export class LoopBreaker {
  static readonly STORAGE_KEY: string = 'loop-breaker-data';
  private static instance: LoopBreaker;

  private readonly reason: AuthRequestReason;
  private readonly clientid: string;
  private readonly time: number;
  private counter: number;
  // acting-on-behalf-of, acting-type, oiamLoginEvent params ...
  private readonly currentTurnData: string;

  constructor(reason: AuthRequestReason, clientid: string, currentTurnData: string = undefined) {
    this.reason = reason;
    this.clientid = clientid;
    this.currentTurnData = currentTurnData;
    this.counter = 1;
    this.time = Date.now();
  }

  static createInstance(reason: AuthRequestReason, eServiceConfig: EServiceConfig, baJwt: BaJwt = undefined) {
    this.instance = new LoopBreaker(
      reason,
      eServiceConfig?.setConfigPayload?.clientid,
      new LoopBreakerData(eServiceConfig, baJwt).toString(),
    );
  }

  /**
   * Check for loop according to the previous authorization request
   * returns true if
   *  - current turn matches the previous one,
   *  - it is at least the third turn with the same reason and data,
   *  - it is done in less than 2s each turn
   * Clean up the saved data in that case.
   *
   * return false in all other cases
   * And save the new data in session storage
   */
  static detectThirdLoop(): boolean {
    if (!this.instance) {
      return false;
    }
    return this.instance._detectThirdLoop();
  }

  _detectThirdLoop(): boolean {
    const savedLoopBreaker: LoopBreaker = this.loadDataFromStorage();
    if (!this.checkLoopCriteria(savedLoopBreaker)) {
      this.saveDataToStorage();
      return false;
    }
    // all criteria match -> check/increment counter
    if (savedLoopBreaker.counter < 3) {
      this.counter = savedLoopBreaker.counter + 1;
      this.saveDataToStorage();
      return false;
    }
    // third turn with same criteria: likely a loop
    this.sendLoopError();
    sessionStorage.removeItem(LoopBreaker.STORAGE_KEY);
    LoopBreaker.instance = undefined;
    return true;
  }

  static clear() {
    sessionStorage.removeItem(LoopBreaker.STORAGE_KEY);
    this.instance = undefined;
  }

  private checkLoopCriteria(savedLoopBreakerData: LoopBreaker): boolean {
    return !(
      !savedLoopBreakerData ||
      // same eService?
      this.clientid !== savedLoopBreakerData.clientid ||
      // time passed to previous turn < 2s?
      this.time - savedLoopBreakerData.time > 2000 ||
      // same reason?
      this.reason !== savedLoopBreakerData.reason ||
      // same data?
      (this.currentTurnData && this.currentTurnData !== savedLoopBreakerData.currentTurnData)
    );
  }

  private saveDataToStorage() {
    const toSaveString = JSON.stringify(this);
    sessionStorage.setItem(LoopBreaker.STORAGE_KEY, toSaveString);
  }

  private loadDataFromStorage(): LoopBreaker {
    const savedString = sessionStorage.getItem(LoopBreaker.STORAGE_KEY);
    if (!savedString) {
      return null;
    }
    try {
      return JSON.parse(savedString);
    } catch (e) {
      log.error(e);
      return null;
    }
  }

  /**
   * Reduce data sent to server for error logging to necessary info and do the call to nginx
   */
  private sendLoopError() {
    let info: any = {};
    const loopBreakerData: LoopBreakerData = LoopBreakerData.fromString(this.currentTurnData);
    if (loopBreakerData) {
      switch (this.reason) {
        case AuthRequestReason.LOGINEVENT:
          info = {
            acr: loopBreakerData.requestedAcrValues,
            customstate: loopBreakerData.requestedCustomstate,
          };
          break;
        case AuthRequestReason.ACCOUNT_LINK:
          info = { accountLink: loopBreakerData.requestedAccountLink };
          break;
        case AuthRequestReason.MISSING_USER:
          info = { clientid: this.clientid };
          break;
        case AuthRequestReason.ACTING_TYPE_CHANGE:
          info = {
            currentActingType: loopBreakerData.currentActingtype,
            requestedActingType: loopBreakerData.requestedAcrValues.actingType,
          };
          break;
        case AuthRequestReason.OTHER_STEPUP:
        case AuthRequestReason.STEPUP:
          info = {
            currentAuthnLevel: loopBreakerData.currentAuthnlevel,
            requestedAuthnLevel: loopBreakerData.requestedAcrValues.authnLevel,
          };
          break;
        case AuthRequestReason.BAFOR_CHANGE:
        case AuthRequestReason.FORCE_BAFOR:
          info = {
            currentBafor: loopBreakerData.currentBafor,
            requestedBafor: loopBreakerData.requestedAcrValues.actingOnBehalfOf,
          };
          break;
        case AuthRequestReason.PROFILE_SELECT:
          info = {
            requestedBafor: loopBreakerData.requestedAcrValues.actingOnBehalfOf,
            requestedActingType: loopBreakerData.requestedAcrValues.actingType,
          };
          break;
      }
    }
    NginxErrorLogger.logError({
      ...ERR_LOOP_DETECTED,
      details: { authflowReason: this.reason, info: info },
    });
  }
}
